/**
 * @module Style
 */
import {
    Circle as CircleStyle,
    RegularShape,
    Icon as OlIcon,
    Fill as OlFill,
    Stroke as OlStroke,
    Text as OlText,
    Style as OlStyle
} from 'ol/style';
import {Options} from 'ol/style/Circle'
import MultiPoint from "ol/geom/MultiPoint";
import {DEVICE_PIXEL_RATIO} from "ol/has";
import {Circle, Geometry, LineString, MultiLineString, MultiPolygon, Point, Polygon} from "ol/geom";
import {
    StyleConfiguration,
    Text,
    Stroke,
    Fill,
    PatternOrientation,
    GeometryType, FilterType, Image, SVGOptions, SymbolDefinition, GroupedType, QuantityInterval
} from "./configuration/style";

import {FlatStyle} from "ol/style/flat";
import {SVG} from "./base/svg"
import Feature from "ol/Feature";
import {ColorLike} from "ol/colorlike";
import {degreesToRadians} from "./general/function";
import {Coordinate} from "ol/coordinate";
import {SVGCallback} from "./general/callback";
import {GeometryFunction, StyleLike} from "ol/style/Style";
import {Icon, Symbol as SymbolConfiguration, GeometryShape} from "./configuration/layer";
import {getSharedEnv, setSharedEnv} from "./general/env.ts";

const imageCache: { [src: string]: HTMLImageElement } = {};

export function preloadImages(sources: string[]): Promise<void[]> {
    const promises = sources.map(src => {
        return new Promise<void>((resolve) => {
            if (imageCache[src]) {
                resolve();
                return;
            }
            const img = new window.Image();
            img.onload = () => {
                imageCache[src] = img;
                resolve();
            };
            img.onerror = () => resolve();
            img.src = src;
        });
    });
    return Promise.all(promises);
}


function createLinePattern(size: number | undefined, lineWidth: number | undefined, strokeColor: ColorLike, orientation: PatternOrientation | undefined) {

    const {canvas, context} = getSharedEnv();

    if (!size || !lineWidth || isNaN(size) || isNaN(lineWidth)) {
        throw new Error('Pattern size and width must be a number.');
    }

    const deviceSize = size * DEVICE_PIXEL_RATIO;
    canvas.width = deviceSize;
    canvas.height = deviceSize;

    let x0;
    let y0;
    let x1;
    let y1;

    switch (orientation) {
        case 'top-left':
            x0 = -2;
            y0 = -2;
            x1 = deviceSize + 2;
            y1 = deviceSize + 2;
            break;
        case 'bottom-left':
            x0 = deviceSize + 2;
            y0 = -2;
            x1 = -2;
            y1 = deviceSize + 2;
            break;
        case 'vertical':
            x0 = DEVICE_PIXEL_RATIO;
            y0 = 0;
            x1 = DEVICE_PIXEL_RATIO;
            y1 = deviceSize;
            break;
        case 'horizontal':
            x0 = 0;
            y0 = DEVICE_PIXEL_RATIO;
            x1 = deviceSize;
            y1 = DEVICE_PIXEL_RATIO;
            break;
        default:
            throw new Error('Pattern orientation is not supported: ' + orientation);
    }

    if (context) {
        context.beginPath();
        context.strokeStyle = strokeColor;
        context.lineWidth = lineWidth;
        context.moveTo(x0, y0);
        context.lineTo(x1, y1);

        // You must fill the top-left & bottom-right corner with a triangle.
        // When repeated as in a pattern, these triangles will fill in the beveled
        // corners caused by your center line coming to a point at top-right & bottom-left
        context.moveTo(x0 - deviceSize, y0);
        context.lineTo(x1 - deviceSize, y1);
        context.moveTo(x0 + deviceSize, y0);
        context.lineTo(x1 + deviceSize, y1);

        context.stroke();

        return context.createPattern(canvas, "repeat");
    }
}

function segmentFilter(coordinate: Coordinate[], filter: FilterType | undefined) {
    if (filter) {
        let filtered: Coordinate[][] = [];

        if (filter.type === 'equal') {

            filter.index.map(c => {
                switch (c) {
                    case 'first':
                        let multiLineFirst: Coordinate[] = [];
                        if (coordinate.length > 1) {
                            multiLineFirst.push(coordinate[0])
                            multiLineFirst.push(coordinate[1])
                            filtered.push(multiLineFirst);
                        }
                        break;
                    case 'last':
                        let multiLineLast = [];
                        if (coordinate.length > 1) {
                            multiLineLast.push(coordinate[coordinate.length - 2])
                            multiLineLast.push(coordinate[coordinate.length - 1])
                            filtered.push(multiLineLast);
                        }
                        break;
                    case 'beforelast':
                        let multiLineBeforeLast: Coordinate[] = [];
                        if (coordinate.length > 2) {
                            multiLineBeforeLast.push(coordinate[coordinate.length - 3]);
                            multiLineBeforeLast.push(coordinate[coordinate.length - 2]);
                            filtered.push(multiLineBeforeLast);
                        }
                        break;
                }
            });
        } else if (filter.type === 'not-equal') {
            filter.index.map(c => {
                switch (c) {
                    case 'first':
                        if (coordinate.length > 1) {
                            coordinate.shift();
                        }
                        break;
                    case 'last':
                        if (coordinate.length > 1) {
                            coordinate.pop();
                        }
                        break;
                    case 'beforelast':
                        if (coordinate.length > 2) {
                            coordinate.splice(coordinate.length - 2, 1);
                        }
                        break;
                }
            })
            filtered.push(coordinate);
        }
        return filtered;
    } else {
        return coordinate;
    }
}

function pointFilter(array: Coordinate[], filter: FilterType) {
    if (filter) {
        let filtered: Coordinate[] = [];

        if (filter.type === 'equal') {
            filter.index.map(c => {
                switch (c) {
                    case 'first':
                        if (array.length > 0) filtered.push(array[0]);
                        break;
                    case 'beforelast':
                        if (array.length >= 2) {
                            filtered.push(array[array.length - 2]);
                        }
                        break;
                    case 'last':
                        if (array.length > 0) {
                            filtered.push(array[array.length - 1])
                        }
                        break;
                }
            });
        } else if (filter.type === 'not-equal') {
            filter.index.map(c => {
                switch (c) {
                    case 'first':
                        if (array.length > 0) {
                            array.shift();
                            filtered = array;
                        }
                        break;
                    case 'beforelast':
                        if (array.length >= 2) {
                            const indexToRemove = array.length - 2;
                            filtered = array.filter((_, idx) => idx !== indexToRemove);
                        } else {
                            filtered = [...array];
                        }
                        break;
                    case 'last':
                        if (array.length > 0) {
                            array.pop();
                            filtered = array;
                        }
                        break;
                }
            })
        }
        return filtered;
    } else {
        return array;
    }
}

function segmentMidpoints(coords: Coordinate[]): Coordinate[] {
    const out: Coordinate[] = [];
    if (!coords || coords.length < 2) return out;

    for (let i = 0; i < coords.length - 1; i++) {
        const a = coords[i];
        const b = coords[i + 1];
        out.push([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]);
    }
    return out;
}

function createImage(image: Image | undefined, feature?: Feature<Geometry>) {

    if (image) {
        switch (image.type) {
            case "regular":
                return new RegularShape({
                    radius: image.radius ?? 0,
                    radius2: image.radius2,
                    points: image.points ?? 0,
                    angle: image.angle,
                    displacement: image.displacement,
                    rotation: image.rotation,
                    rotateWithView: image.rotateWithView,
                    scale: image.scale,
                    stroke: new OlStroke({
                        color: image.stroke?.color,
                        width: image.stroke?.width
                    }),
                    fill: new OlFill({
                        color: image.fill?.color
                    })
                });
            case "circle":
                let options: Options = {
                    radius: image.radius ?? 0
                };

                if (image.stroke !== undefined) {
                    options['stroke'] = new OlStroke({
                        color: image.stroke.color !== undefined ? image.stroke.color : 'rgba(0,0,0,0)',
                        width: image.stroke.width !== undefined ? image.stroke.width : 0
                    })
                }

                if (image.fill !== undefined) {
                    options['fill'] = new OlFill({
                        color: image.fill.color
                    })
                }
                return new CircleStyle(options);
            case "icon":
                let rotation;

                if (image.rotationProperty !== undefined && feature) {
                    rotation = feature.get(image.rotationProperty)
                    rotation = degreesToRadians(rotation);
                }

                const cachedImg = image.src ? imageCache[image.src] : undefined;

                return new OlIcon({
                    img: cachedImg,
                    src: cachedImg ? undefined : image.src,
                    color: image.color,
                    opacity: image.opacity,
                    anchor: image.anchor,
                    displacement: image.displacement,
                    rotation: rotation,
                    declutterMode: image.declutterMode ?? "none"
                })
            default:
                return null;
        }
    }
}

function createStroke(stroke: Stroke | undefined) {
    return new OlStroke({
        color: (stroke !== undefined && stroke.color !== undefined) ? stroke.color : undefined,
        width: (stroke !== undefined && stroke.width !== undefined) ? stroke.width : undefined,
        lineCap: (stroke !== undefined && stroke.lineCap !== undefined) ? stroke.lineCap : undefined,
        lineJoin: (stroke !== undefined && stroke.lineJoin !== undefined) ? stroke.lineJoin : undefined,
        lineDash: (stroke !== undefined && stroke.lineDash !== undefined) ? stroke.lineDash : undefined
    })
}

function createIconPattern(src: string | undefined, size: number = 32, backgroundColor?: string, opacity: number = 1) {
    if (!src) return null;

    const img = imageCache[src];
    if (!img) return null;

    const { context } = getSharedEnv();
    if (!context) return null;

    const patternCanvas = document.createElement('canvas');
    const pCtx = patternCanvas.getContext('2d');
    if (!pCtx) return null;

    const deviceSize = size * DEVICE_PIXEL_RATIO;
    patternCanvas.width = deviceSize;
    patternCanvas.height = deviceSize;

    if (backgroundColor) {
        pCtx.fillStyle = backgroundColor;
        pCtx.fillRect(0, 0, deviceSize, deviceSize);
    }

    pCtx.globalAlpha = opacity;
    const iconSize = deviceSize * 0.6;
    const offset = (deviceSize - iconSize) / 2;
    pCtx.drawImage(img, offset, offset, iconSize, iconSize);
    pCtx.globalAlpha = 1.0;

    return context.createPattern(patternCanvas, 'repeat');
}

function createFill(fill: Fill | undefined) {
    if (fill !== undefined) {
        if (fill.type === 'line-pattern') {
            const pattern = createLinePattern(fill.size, fill.width, fill.color, fill.orientation);
            return new OlFill({color: pattern});
        } else if (fill.type === 'icon-pattern') {
            const pattern = createIconPattern(fill.src, fill.size, fill.color, fill.opacity);
            return new OlFill({color: pattern || undefined});
        } else if (fill.color !== undefined) {
            return new OlFill({color: fill.color});
        } else {
            return null;
        }
    }
}

function createTextStyle(text: Text | undefined) {
    let textStyle = null;
    if (text) {
        textStyle = new OlText({
            textAlign: text.textAlign,
            textBaseline: text.textBaseline,
            placement: text.placement,
            fill: new OlFill({
                color: (text.fill !== undefined && text.fill.color !== undefined) ? text.fill.color : null
            }),
            backgroundFill: new OlFill({
                color: (text.backgroundFill !== undefined && text.backgroundFill.color !== undefined) ? text.backgroundFill.color : 'rgba(0,0,0,0)'
            }),
            stroke: new OlStroke({
                color: (text.stroke !== undefined && text.stroke.color !== undefined) ? text.stroke.color : undefined,
                width: (text.stroke !== undefined && text.stroke.width !== undefined) ? text.stroke.width : undefined
            }),
            backgroundStroke: new OlStroke({
                color: (text.backgroundStroke !== undefined && text.backgroundStroke.color !== undefined) ? text.backgroundStroke.color : 'rgba(0,0,0,0)',
                width: (text.backgroundStroke !== undefined && text.backgroundStroke.width !== undefined) ? text.backgroundStroke.width : 0
            }),
            offsetX: text.offsetX,
            offsetY: text.offsetY,
            rotation: text.rotation,
            font: text.font,
            padding: text.padding,
            overflow: text.overflow !== undefined ? text.overflow : false
        });
    }

    return textStyle;
}

function createGeometryFunction(config: GeometryType | undefined) {
    if (!config) return;

    return (feature: Feature<Geometry>) => {
        const geometry = feature.getGeometry();

        switch (config.conversion) {
            case "segment": {
                if (geometry instanceof LineString) {
                    const line = geometry.getCoordinates() as Coordinate[];
                    const segments = segmentFilter(line, config.filter) as Coordinate[][];
                    return new MultiLineString(segments);
                }
                if (geometry instanceof MultiLineString) {
                    const lines = geometry.getCoordinates() as Coordinate[][];
                    const segments = lines.flatMap((line) =>
                        segmentFilter(line, config.filter) as Coordinate[][]
                    );
                    return new MultiLineString(segments);
                }

                if (geometry instanceof Polygon) {
                    const rings = geometry.getCoordinates() as Coordinate[][];
                    const segments = rings.flatMap((ring) =>
                        segmentFilter(ring, config.filter) as Coordinate[][]
                    );
                    return new MultiLineString(segments);
                }
                if (geometry instanceof MultiPolygon) {
                    const polygons = geometry.getCoordinates() as Coordinate[][][];
                    const segments = polygons.flatMap((rings) =>
                        rings.flatMap((ring) => segmentFilter(ring, config.filter) as Coordinate[][])
                    );
                    return new MultiLineString(segments);
                }
                break;
            }

            case "vertex": {
                if (geometry instanceof Polygon) {
                    const rings = geometry.getCoordinates() as Coordinate[][];
                    let points: Coordinate[] = [];
                    rings.forEach((ring) => {
                        const processed = config.filter ? pointFilter(ring, config.filter) : ring;
                        points = points.concat(processed);
                    });
                    return new MultiPoint(points);
                }
                if (geometry instanceof MultiPolygon) {
                    const polygons = geometry.getCoordinates() as Coordinate[][][];
                    let points: Coordinate[] = [];
                    polygons.forEach((rings) => {
                        rings.forEach((ring) => {
                            const processed = config.filter ? pointFilter(ring, config.filter) : ring;
                            points = points.concat(processed);
                        });
                    });
                    return new MultiPoint(points);
                }
                if (geometry instanceof LineString) {
                    let points = geometry.getCoordinates() as Coordinate[];
                    if (config.filter) points = pointFilter(points, config.filter);
                    return new MultiPoint(points);
                }
                if (geometry instanceof MultiLineString) {
                    const lines = geometry.getCoordinates() as Coordinate[][];
                    let points: Coordinate[] = [];
                    lines.forEach((line) => {
                        const processed = config.filter ? pointFilter(line, config.filter) : line;
                        points = points.concat(processed);
                    });
                    return new MultiPoint(points);
                }
                break;
            }

            case "midpoint": {
                if (geometry instanceof Polygon) {
                    const rings = geometry.getCoordinates() as Coordinate[][];
                    let midpoints: Coordinate[] = [];
                    rings.forEach((ring) => {
                        let ringMids = segmentMidpoints(ring);
                        if (config.filter) ringMids = pointFilter([...ringMids], config.filter);
                        midpoints = midpoints.concat(ringMids);
                    });
                    return new MultiPoint(midpoints);
                }
                if (geometry instanceof MultiPolygon) {
                    const polys = geometry.getCoordinates() as Coordinate[][][];
                    let midpoints: Coordinate[] = [];
                    polys.forEach((rings) => {
                        rings.forEach((ring) => {
                            let ringMids = segmentMidpoints(ring);
                            if (config.filter) ringMids = pointFilter([...ringMids], config.filter);
                            midpoints = midpoints.concat(ringMids);
                        });
                    });
                    return new MultiPoint(midpoints);
                }
                if (geometry instanceof LineString) {
                    let midpoints = segmentMidpoints(geometry.getCoordinates() as Coordinate[]);
                    if (config.filter) midpoints = pointFilter([...midpoints], config.filter);
                    return new MultiPoint(midpoints);
                }
                if (geometry instanceof MultiLineString) {
                    const lines = geometry.getCoordinates() as Coordinate[][];
                    let midpoints: Coordinate[] = [];
                    lines.forEach((line) => {
                        let lineMidpoints = segmentMidpoints(line);
                        if (config.filter) lineMidpoints = pointFilter([...lineMidpoints], config.filter);
                        midpoints = midpoints.concat(lineMidpoints);
                    });
                    return new MultiPoint(midpoints);
                }
                break;
            }

            case "center": {
                // A Circle's center is the natural anchor for a midpoint marker.
                // OpenLayers does not render image/point styles on Circle
                // geometries, so converting to a Point lets the symbol show up.
                if (geometry instanceof Circle) {
                    const center = geometry.getCenter();
                    if (center) return new Point(center);
                }
                // Polygons fall back to their interior point so the same style
                // can mark the visual center of an area feature.
                if (geometry instanceof Polygon) {
                    return geometry.getInteriorPoint();
                }
                if (geometry instanceof MultiPolygon) {
                    return geometry.getInteriorPoints();
                }
                break;
            }
        }

        return undefined;
    };
}

export function createOlStyle(config: StyleConfiguration | SymbolDefinition | GroupedType, feature?: Feature<Geometry>) {
    return new OlStyle({
        image: createImage(config.image, feature) || undefined,
        stroke: createStroke(config.stroke) || undefined,
        fill: createFill(config.fill) || undefined,
        text: createTextStyle(config.text) || undefined,
        geometry: createGeometryFunction(config.geometry) as GeometryFunction || undefined,
        zIndex: config.zIndex ? config.zIndex : 0
    });
}

class Style {
    private readonly SVG;

    private cache: { [key: string]: OlStyle } = {};

    private type = {
        GEOJSON: "geojson",
        CLUSTER: "cluster",
        GEOLOCATION: "geolocation",
        WEBGLPOINTS: "webglpoints"
    }

    private OTHER_DEFAULT_CATEGORY = "other_default_category";
    private config!: StyleConfiguration[];
    private geojson: { [key: string]: StyleLike } = {};
    private cluster: { [key: string]: StyleLike } = {};
    private geolocation: { [key: string]: StyleLike } = {};
    private webglpoints: { [key: string]: FlatStyle } = {};


    private readonly canvas: HTMLCanvasElement;
    private readonly context: CanvasRenderingContext2D | undefined;

    constructor() {

        this.SVG = new SVG();
        this.canvas = document.createElement("canvas");
        this.context = this.canvas.getContext("2d") as CanvasRenderingContext2D;
        setSharedEnv({canvas: this.canvas, context: this.context});
    }

    /**
     * Initialization of the style object.
     *
     * Icon preloading warms the browser's HTTP cache so OpenLayers' lazy
     * `OlIcon` loader gets a cache hit on first render — without it, point
     * icons can flash empty before the network fetch completes.
     *
     * @param config - Style configuration
     * @param waitForPreload - When `true` (default), the call awaits
     *   `preloadImages` before building style functions. When `false`, the
     *   preload is fired in the background and style functions are built
     *   immediately; OL will re-render layers as each icon finishes loading.
     *   Pass `false` from environments that cannot observe a returned Promise
     *   (e.g. Android WebView `evaluateJavascript`).
     */
    public async init(config: StyleConfiguration[], waitForPreload: boolean = true) {

        this.config = config;

        const iconSources = this.collectIconSources(config);
        if (iconSources.length > 0) {
            if (waitForPreload) {
                await preloadImages(iconSources);
            } else {
                // Fire-and-forget: warms the HTTP cache so OL picks up icons
                // on its next render pass. No await — caller cannot wait.
                preloadImages(iconSources);
            }
        }

        config.map((style: StyleConfiguration) => {
            this.create(style);
        })
    }

    /**
     * Create style function
     * Used for testing mainly.
     * @private
     * @param style - Style configuration
     */
    public create(style: StyleConfiguration) {
        switch (style.type) {
            case this.type.GEOLOCATION:
                this.geolocation[style.name] = this.createStyleFunction(style) as StyleLike;
                break;
            case this.type.GEOJSON:
                this.geojson[style.name] = this.createStyleFunction(style) as StyleLike;
                break;
            case this.type.CLUSTER:
                this.cluster[style.name] = this.createStyleFunction(style) as StyleLike;
                break;
            case this.type.WEBGLPOINTS:
                this.webglpoints[style.name] = style.flat as FlatStyle;
                break;
        }
    };

    /**
     * Returns defined style
     * @private
     * @param name Style name
     * @returns return Ol style
     */
    public getStyle(name: string) {
        if (this.geolocation[name]) {
            return this.geolocation[name];
        } else if (this.geojson[name]) {
            return this.geojson[name];
        } else if (this.cluster[name]) {
            return this.cluster[name];
        } else if (this.webglpoints[name]) {
            return this.webglpoints[name];
        } else {
            throw new Error('No such style defined.');
        }
    }

    /**
     * Returns defined symbology
     * @private
     * @param name Style name {@link styleConfig}
     * @returns return Style of the layer
     */
    public getLayerConfig(name: string) {
        return this.config.filter((style: StyleConfiguration) => style.name === name)[0];
    }

    /**
     * Style in config
     *"attribute": {
     *  "columns": ["standnumber", "standnumberextension"],
     *  "separator": "."
     *}
     * or
     *"attribute": {
     *  "columns": ["standnumber", "standnumberextension"],
     *  "fonts": ['12px "Font Awesome 7 Pro"', "bold 12px Verdana"],
     *}
     */
    private formatTextWithSeparator(style: OlStyle, styleDefinition: SymbolDefinition | StyleConfiguration | GroupedType, feature: Feature<Geometry>, resolution: number) {
        if (styleDefinition.text && feature) {
            const attribute = styleDefinition.text.attribute;
            let text: string | string[] = '';
            if (attribute.columns.length === 1) {
                let labelValue = feature.get(attribute.columns[0])
                if (!labelValue) {
                    labelValue = '';
                }
                text = String(labelValue);
            } else if (
                Array.isArray(attribute.columns) &&
                Array.isArray(attribute.fonts) &&
                attribute.columns.length > 0 &&
                attribute.fonts.length > 0 &&
                attribute.columns.length === attribute.fonts.length) {
                text = [];
                for (let i = 0; i < attribute.columns.length; i++) {
                    let labelValue = feature.get(attribute.columns[i])
                    if (!labelValue) {
                        labelValue = '';
                    }
                    text.push(labelValue + ' ');
                    text.push(attribute.fonts[i]);
                }
            } else {
                const separator = attribute.separator ? attribute.separator : '';
                attribute.columns.map((column: string) => {
                    if (feature.get(column)) {
                        text += String(feature.get(column));
                        text += separator;
                    }
                });
                text = text.substring(0, text.lastIndexOf(separator));
            }
            const olStyleText = style.getText();
            if (!styleDefinition.text.maxResolution || resolution < styleDefinition.text.maxResolution) {
                olStyleText?.setText(text);
            } else {
                olStyleText?.setText('');
            }
        }
    };

    private collectIconSources(config: StyleConfiguration[]): string[] {
        const sources = new Set<string>();

        const extract = (symbol: any) => {
            if (!symbol) return;

            if (symbol.fill?.type === 'icon-pattern' && symbol.fill.src) {
                sources.add(symbol.fill.src);
            }

            if (symbol.image?.type === 'icon' && symbol.image.src) {
                sources.add(symbol.image.src);
            }

            if (symbol.categorized) {
                handleCategorized(symbol.categorized);
            }
        };

        const handleCategorized = (categorized: any) => {
            if (categorized.category) {
                Object.values(categorized.category).forEach(symbol => extract(symbol));
            }
            if (categorized.intervals) {
                categorized.intervals.forEach((interval: any) => extract(interval.symbol));
            }
            if (categorized.default) {
                extract(categorized.default);
            }
        };

        config.forEach(style => {
            extract(style);

            if (style.grouped) {
                style.grouped.forEach(group => extract(group));
            }

            if (style.categorized) {
                handleCategorized(style.categorized);
            }
        });

        return Array.from(sources);
    }

    private matchQuantityInterval(
        rawValue: unknown,
        intervals: QuantityInterval[]
    ): QuantityInterval | undefined {
        if (rawValue === null || rawValue === undefined) return;

        let value: number;

        if (typeof rawValue === "number") {
            value = rawValue;
        } else if (typeof rawValue === "string") {
            value = Number(rawValue.replace(",", "."));
        } else {
            return;
        }

        if (Number.isNaN(value)) return;

        return intervals.find(i =>
            (i.from === undefined || value >= i.from) &&
            (i.to === undefined || value < i.to)
        );
    }

    private createCachedStyle(style: StyleConfiguration | GroupedType, feature: Feature<Geometry>, resolution: number) {
        let cacheID = style.name;
        let featureStyleDefinition: StyleConfiguration | SymbolDefinition | GroupedType | undefined;

        if (style.type === "cluster") {
            const size = feature.get("features").length;
            feature.set("cluster_size", size);
        }

        if (style.categorized && feature) {
            const column = style.categorized.column;
            const value = feature.get(column);

            if ("category" in style.categorized) {
                const category = style.categorized.category;
                const symbol = category[String(value)];

                if (symbol) {
                    cacheID = style.name + "_" + String(value);
                    featureStyleDefinition = symbol;
                } else if (category[this.OTHER_DEFAULT_CATEGORY]) {
                    cacheID = style.name + "_" + this.OTHER_DEFAULT_CATEGORY;
                    featureStyleDefinition = category[this.OTHER_DEFAULT_CATEGORY];
                }
            }

            if ("intervals" in style.categorized) {
                const match = this.matchQuantityInterval(
                    Number(value),
                    style.categorized.intervals
                );

                if (match) {
                    const from = match.from ?? "min";
                    const to = match.to ?? "max";
                    cacheID = `${style.name}_q_${from}_${to}`;
                    featureStyleDefinition = match.symbol;
                } else if (style.categorized.default) {
                    cacheID = `${style.name}_q_default`;
                    featureStyleDefinition = style.categorized.default;
                }
            }
        }

        if (!this.cache[cacheID]) {
            const def = featureStyleDefinition ?? style;

            if (!def.image && !def.stroke && !def.fill && !def.text) {
                this.cache[cacheID] = new OlStyle(undefined);
            } else {
                this.cache[cacheID] = createOlStyle(def, feature);
            }
        }

        featureStyleDefinition = featureStyleDefinition ?? style;
        this.formatTextWithSeparator(this.cache[cacheID], featureStyleDefinition, feature, resolution);

        return this.cache[cacheID];
    }


    private cacheStyle(config: StyleConfiguration | GroupedType, feature: Feature<Geometry>, resolution: number) {
        let olStyle;

        if (config.cached === false) {
            olStyle = createOlStyle(config, feature);
        } else {
            olStyle = this.createCachedStyle(config, feature, resolution);
        }

        return olStyle;
    }

    private createStyleFunction(config: StyleConfiguration) {

        return (feature: Feature<Geometry>, resolution: number) => {
            let cachedStyle: OlStyle[];

            if (config.grouped) {
                cachedStyle = [];
                let number = 0;
                config.grouped.map((group: GroupedType) => {
                    group['name'] = config.name + String(number);
                    group['type'] = config.type;
                    const olStyle = this.cacheStyle(group, feature, resolution)
                    cachedStyle.push(olStyle);
                    number++;
                })
                return cachedStyle;
            } else {
                return this.cacheStyle(config, feature, resolution);
            }
        }
    };

    /**
     * Create SVG for unique style (e.g. polygon, point, triangle, line)
     * @param geometry - Symbol geometry type: point, polygon
     * @param symbol - Symbol configuration
     * @param callback
     * @param options
     * @returns SVG
     */
    public createUniqueSVG(geometry: GeometryShape, symbol: SymbolConfiguration & Icon, callback: SVGCallback | undefined, options?: SVGOptions | undefined) {
        return this.SVG.unique(geometry, symbol, callback, options)
    }

    /**
     * Create SVG for ramp style (quantity representation)
     * @param steps - RampSteps configuration {@link layerConfig}
     * @param callback
     * @param options
     * @returns SVG
     */
    public createContinuousSVG(steps: StyleConfiguration[], callback: SVGCallback | undefined, options?: SVGOptions | undefined) {
        return this.SVG.continuous(steps, callback, options)
    }

    /**
     * Clear registered styles
     */
    public clear() {
        this.config = [];
        this.geojson = {};
        this.cluster = {};
        this.geolocation = {};
        this.webglpoints = {};
        this.cache = {};
    }
}

export default Style;
